import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { Typography } from '@pesto/ui/components/Typography';
import { z } from 'zod';

import type { ModalProps } from '../types/modal.types';

import { useUpdateUserMutation } from '../../__generated__/graphql/api';
import { getUserMutationRefreshOptions } from '../../utils/userCacheInvalidation';
import { createFormComponent } from '../utils/create-form-component';

const schema = z.object({});

type ToggleFriendsAndFamilyForm = z.infer<typeof schema>;

export const ToggleFriendsAndFamilyComponent = ({
  payload,
  api,
}: ModalProps<{ userId: string; isFriendsAndFam: boolean }>) => {
  const { userId, isFriendsAndFam } = payload;
  const { handleMutation } = useMutationWithToast();

  const [updateUser, { loading }] = useUpdateUserMutation({
    errorPolicy: 'all',
    ...getUserMutationRefreshOptions(userId),
  });

  const handleSubmit = () => {
    void handleMutation({
      mutation: () =>
        updateUser({
          variables: {
            userId,
            isFriendsAndFam,
          },
        }),
      extractSuccess: (result) => !!result?.data,
      onSuccess: () => api.close(),
      debugLabel: 'toggle-friends-and-family',
      toast: {
        success: isFriendsAndFam ? 'User marked as Friends & Family' : 'User removed from Friends & Family',
        error: 'Failed to update Friends & Family status',
        loading: 'Updating Friends & Family status...',
      },
    });
  };

  const config: SmartFormConfig<ToggleFriendsAndFamilyForm> = {
    schema,
    defaultValues: {},
    submitLabel: isFriendsAndFam ? 'Mark as Friends & Family' : 'Remove Friends & Family',
  };

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="body" className="text-muted-foreground text-sm">
        {isFriendsAndFam
          ? 'This will mark the user as Friends & Family.'
          : 'This will remove the user from Friends & Family.'}
      </Typography>
      {createFormComponent({
        config,
        handleSubmit,
        loading,
      })}
    </div>
  );
};
